import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { DocumentData } from 'firebase/firestore';

import CustomDatePicker from '../../../uiParts/form/CustomDatePicker';
import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import PlainCellInput from './PlainCellInput';
import ChoiceField from '../../../uiParts/form/ChoiceField';
import MatchReasonLabel from '../../../uiParts/MatchReasonLabel';
import {
  gray5,
  gray40,
  orange50,
  red50,
  secondaryButtonBackgroundColor,
} from '../../../../constants/colors';
import {
  OCR_AZURE_CONFIDENCE_THRESHOLD,
  OCR_GEMINI_LOGPROBS_THRESHOLD,
  processingStatus,
} from '../../../../constants/enum';
import { isChoiceDataType } from '../../../../constants/itemValidation';
import { arrayCalculationCellKey } from '../../../../utils/pattern/resultCalculation';
import {
  getRowId,
  arrayFieldErrorKey,
  masterSelectionKey,
  reviewIssuePathKey,
} from '../../../../utils/pattern/detailRowKeys';
import type { CalculationCellStates } from '../../../../utils/pattern/resultCalculation';
import { buildCellReviewReasons } from '../../../../utils/pattern/reviewReasons';
import { isOperatorStaff } from '../../../../utils/permission';
import type { ReviewIssue } from '../../../../types/reviewIssue';
import type { ItemListChildType, ItemListType } from '../../../../types/pattern';
import type { MasterWithMappingType } from '../../../../types/master';
import type {
  MasterFilterCondition,
  MatchFilterValue,
  ReconciliationTarget,
} from '../../../../types/masterReconciliation';
import type { DictionaryWithMappingType } from '../../../../types/dictionary';
import type { UserSetType } from '../../../../types/user';
import type {
  DynamicFieldValue,
  DynamicFormData,
} from '../../../../api/firebase/firestore/result/update';

/**
 * 明細テーブルの照合で選択されたマスタ情報（`selectedMasterIds` の値）。
 * PatternAiOcrResult 側の state と同じ形。
 */
export type SelectedMasterInfo = {
  ocr_value: string;
  matched_label: string;
  label: string;
  source_field_name: string;
  master_record_id?: number;
  match_reason_label?: string;
  // スコープ（取引先など）の値。設定時のみ。履歴保存・確認モーダル表示に使う。
  supplier_id?: string;
  // スコープ項目の名称（例：取引先コード）。確認モーダル表示用。
  supplier_label?: string;
  // 照合キー項目の item_id（保存時に result_data のどの項目へラベルを書くか）（#3078）。
  search_item_id?: string;
  // スコアラベル（例："85%"）。保存時に result_data へ書き込む（#3078）。
  score?: string;
  // 反映対象の行インデックス（配列項目の場合。単一値は 0）（#3078）。
  row_index?: number;
  // 選択で埋める値（column_mappings 由来）。保存時に result_data へ反映する（#3078）。
  applied_values?: { item_id: string; value: unknown }[];
  // 再照合で使った絞り込みの値。保存時に result_data へ書き込み、照合理由に出す（#3517）。
  match_filter_values?: MatchFilterValue[];
};

/** 辞書変換モーダルを開くときの対象情報（`dictionaryReconciliationTarget` の値）。 */
export type DictionaryReconciliationTargetType = {
  dictionary: DictionaryWithMappingType;
  rowIndex?: number;
  ocrValue?: string;
  // 入力欄の現在値。モーダルの初期検索クエリに使う（ユーザーが入力し直した値で再検索）。
  searchQuery?: string;
};

/**
 * 行内で使う `styles` の一部（PatternAiOcrResult のローカル定数から複製）。
 * 行コンポーネント側でしか使わないため、ここに閉じて持つ。
 */
const rowStyles = {
  addButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },
  removeButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },
  reReadingOverlay: {
    position: 'relative' as const,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(226, 232, 240, 0.7)',
      zIndex: 1,
    },
    // 素の input に置き換えたセル（#3275）はインラインスタイルが優先されるため
    // ここでは効かない。PlainCellInput 側の dimmed で対応している
    '& .MuiInputBase-root': {
      color: 'rgba(10, 10, 10, 0.3)',
    },
  },
};

// 列幅設定に応じた最低幅を返すヘルパー関数（PatternAiOcrResult と共有）
export type ColumnWidthResolver = (displayWidth?: number) => number;

export interface DetailTableRowProps {
  /** この行のフォーム値 */
  item: DynamicFieldValue;
  /** 明細内の行インデックス */
  itemIndex: number;
  /** 明細（配列項目）の親項目 */
  itemGroup: ItemListType;
  /** 表示対象の子項目（is_active のみ） */
  activeChildren: ItemListChildType[];
  /** activeChildren の flex 比率合計 */
  totalFlex: number;
  /** textarea 型の子項目があるか */
  hasTextareaChild: boolean;
  /** Firestore result_data */
  fieldResultData: Record<string, unknown> | undefined;
  dynamicFormData: DynamicFormData;
  arrayFieldErrors: Record<string, string>;
  calculationCellStates: CalculationCellStates;
  mastersBySearchItemId: Map<string, MasterWithMappingType>;
  dictionariesBySearchItemId: Map<string, DictionaryWithMappingType>;
  masterMappingTargetIds: Set<string>;
  dictionaryMappingTargetIds: Set<string>;
  selectedMasterIds: Map<string, SelectedMasterInfo>;
  unlinkedMasterKeys: Set<string>;
  cellReviewIssuesByPath: Map<string, ReviewIssue[]>;
  reReadingItemIds: string[];
  isProcessingStatus: number | null;
  resultRecord: DocumentData | undefined;
  user: UserSetType | null;
  nextRowIdCounter: number;
  /** 列幅ヘルパー（PatternAiOcrResult から渡す） */
  getColumnMinWidth: ColumnWidthResolver;
  getColumnFlex: ColumnWidthResolver;
  /** 空行を作るヘルパー（PatternAiOcrResult から渡す） */
  createEmptyRow: (children: ItemListChildType[], rowId: number) => DynamicFieldValue;
  /** 明細セル1つの値を差し替えるヘルパー（PatternAiOcrResult から渡す） */
  updateCellValue: (
    formData: DynamicFormData,
    parentValue: string,
    rowIndex: number,
    childValue: string,
    value: string | number | Date | null,
  ) => DynamicFormData;
  setDynamicFormData: React.Dispatch<React.SetStateAction<DynamicFormData>>;
  /** フォーカス中の項目キーを設定する（#3275 で Context 管理に変更） */
  setFocusItem: (value: string) => void;
  setNextRowIdCounter: React.Dispatch<React.SetStateAction<number>>;
  setReconciliationTarget: React.Dispatch<React.SetStateAction<ReconciliationTarget | null>>;
  setMasterModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDictionaryReconciliationTarget: React.Dispatch<
    React.SetStateAction<DictionaryReconciliationTargetType | null>
  >;
  setDictionaryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  validateArrayField: (
    parentValue: string,
    itemIndex: number,
    childValue: string,
    value: string | number | Date | null,
    child: ItemListChildType,
  ) => string | undefined;
  updateArrayFieldError: (
    parentValue: string,
    itemIndex: number,
    childValue: string,
    errorMessage: string | undefined,
  ) => void;
  /** 削除した行に紐づく画面状態を破棄する（#3283） */
  discardRowState: (itemGroup: ItemListType, rowId: number) => void;
  requestRecalculation: () => void;
  requestRecalculationForItem: (itemId: string) => void;
  resolveSupplierId: (master: MasterWithMappingType, rowIndex?: number) => string | undefined;
  resolveFilterConditions: (
    master: MasterWithMappingType,
    rowIndex?: number,
  ) => MasterFilterCondition[];
}

/**
 * 明細テーブルのデータ行1行分。
 *
 * PatternAiOcrResult から切り出した（表示・挙動は据え置き）。
 * React.memo で包み、編集した行以外の再描画を抑える。
 */
const DetailTableRow = ({
  item,
  itemIndex,
  itemGroup,
  activeChildren,
  totalFlex,
  hasTextareaChild,
  fieldResultData,
  dynamicFormData,
  arrayFieldErrors,
  calculationCellStates,
  mastersBySearchItemId,
  dictionariesBySearchItemId,
  masterMappingTargetIds,
  dictionaryMappingTargetIds,
  selectedMasterIds,
  unlinkedMasterKeys,
  cellReviewIssuesByPath,
  reReadingItemIds,
  isProcessingStatus,
  resultRecord,
  user,
  nextRowIdCounter,
  getColumnMinWidth,
  getColumnFlex,
  createEmptyRow,
  updateCellValue,
  setDynamicFormData,
  setFocusItem,
  setNextRowIdCounter,
  setReconciliationTarget,
  setMasterModalOpen,
  setDictionaryReconciliationTarget,
  setDictionaryModalOpen,
  validateArrayField,
  updateArrayFieldError,
  discardRowState,
  requestRecalculation,
  requestRecalculationForItem,
  resolveSupplierId,
  resolveFilterConditions,
}: DetailTableRowProps): React.ReactElement => {
  // 行に紐づく状態・保存済みデータの参照に使う行ID（#3283）。
  // 配列位置（itemIndex）だと行の挿入・削除で他の行にズレる。
  //
  // fieldResultData（result_data）や review_issues は「保存時点のスナップショット」で、
  // 画面で行を挿入・削除しても中身は動かない。そのため画面の配列位置ではなく、
  // 保存時点の位置＝行ID で引く必要がある。
  // itemIndex を使ってよいのは dynamicFormData（画面の現在の並び）だけ。
  const rowId = getRowId(item, itemIndex);

  // この行のいずれかのセルに照合理由が表示されるなら、
  // 絶対配置の Typography 分の高さを確保するため余白を追加する。
  // 手動再照合の selectedMasterIds と、自動照合で書き込まれた
  // Firestore result_data 内のラベル、どちらかがあれば true。
  const hasMatchReasonRow = itemGroup.children.some((cf: ItemListChildType) => {
    // マスタ照合・辞書変換のどちらかに紐づく項目を対象にする
    const isVerificationCell =
      mastersBySearchItemId.has(cf.id) || dictionariesBySearchItemId.has(cf.id);
    if (!isVerificationCell) return false;
    const master = mastersBySearchItemId.get(cf.id);
    if (master) {
      const mapKey = masterSelectionKey(master.master_id, rowId);
      if (selectedMasterIds.get(mapKey)?.match_reason_label) {
        return true;
      }
    }
    const firestoreLabel = (
      (fieldResultData?.[itemGroup.id] as Array<Record<string, unknown>> | undefined)?.[rowId]?.[
        cf.id
      ] as { match_reason_label?: string } | undefined
    )?.match_reason_label;
    return !!firestoreLabel;
  });
  // この行にバリデーションエラー（#2815）または計算エラー（#3172）が
  // あるか。ある行はセルを縦に伸ばし、メッセージを区切り線の内側に収める
  const hasErrorRow = itemGroup.children.some(
    (cf: ItemListChildType) =>
      !!arrayFieldErrors[arrayFieldErrorKey(itemGroup.parentValue, rowId, cf.childValue)] ||
      !!calculationCellStates[arrayCalculationCellKey(itemGroup.id, rowId, cf.id)]?.errorMessage ||
      // 要注意（#3262）もセル下部に1行増えるため高さ確保の対象にする
      cellReviewIssuesByPath.has(reviewIssuePathKey(itemGroup.id, rowId, cf.id)),
  );
  return (
    <Box
      sx={{
        display: 'flex',
        // 照合理由・エラーがあるセルは縦に伸びるため上揃え、なければ従来挙動
        alignItems: hasTextareaChild || hasMatchReasonRow || hasErrorRow ? 'flex-start' : 'center',
        borderBottom: `1px solid ${gray40}`,
      }}
    >
      {activeChildren.map((childField: ItemListChildType, fieldIndex: number) => {
        const flexValue = getColumnFlex(childField.displayWidth);
        const widthPercent = (flexValue / totalFlex) * 100;
        const isColumnReReading = reReadingItemIds.includes(childField.id);
        // 照合理由ラベルの表示判定用。
        // マスタ照合・辞書変換のどちらかの対象セルならラベルを表示する。
        // （照合/変換ボタンの表示判定はそれぞれ専用 Map で別途行う）
        const isSearchKey =
          mastersBySearchItemId.has(childField.id) || dictionariesBySearchItemId.has(childField.id);
        // result_data の配列行から is_uncertain を取得
        const arrayResultData = fieldResultData?.[itemGroup.id] as
          | Array<Record<string, unknown>>
          | undefined;
        const cellEntry = arrayResultData?.[rowId]?.[childField.id] as
          | {
              is_uncertain?: boolean;
              confidence?: number;
              avg_logprobs?: number;
            }
          | undefined;
        const isCellUncertain =
          (cellEntry?.is_uncertain ?? false) &&
          (isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing);
        // 要注意（#3262）。読み取り信頼度だけでなく、バリデーション・
        // 照合スコア由来の判定もここで拾う
        const cellReviewPath = reviewIssuePathKey(itemGroup.id, rowId, childField.id);
        const cellReviewIssues = cellReviewIssuesByPath.get(cellReviewPath) ?? [];
        // 計算セル（#3172）。計算値採用中は読み取り専用にする。
        // disabled ではなく readOnly にして計算結果をコピーできるようにする
        const cellCalculationState =
          calculationCellStates[arrayCalculationCellKey(itemGroup.id, rowId, childField.id)];
        const isCellCalculationLocked = cellCalculationState?.isCalculated === true;
        // 自動入力（マスタ照合・辞書変換）または計算値採用中は背景をグレーにする。
        // 素の input に置き換えたセルは MUI のセレクタが効かないため、
        // 判定を切り出して PlainCellInput の filled にも渡す（#3275）
        const isAutoFilledCell =
          masterMappingTargetIds.has(childField.id) ||
          dictionaryMappingTargetIds.has(childField.id) ||
          isCellCalculationLocked;
        const cellTooltip =
          isCellUncertain && isOperatorStaff(user?.role)
            ? ((): string => {
                const lines: string[] = [];
                if (
                  cellEntry?.confidence !== undefined &&
                  cellEntry.confidence < OCR_AZURE_CONFIDENCE_THRESHOLD
                ) {
                  lines.push(`Azure confidence: ${cellEntry.confidence.toFixed(3)}`);
                }
                if (
                  cellEntry?.avg_logprobs !== undefined &&
                  cellEntry.avg_logprobs < OCR_GEMINI_LOGPROBS_THRESHOLD
                ) {
                  lines.push(`Gemini avgLogprobs: ${cellEntry.avg_logprobs.toFixed(3)}`);
                }
                return lines.join('\n');
              })()
            : '';

        return (
          <Box
            key={fieldIndex}
            // 要注意サマリーからのスクロール先（#3262）
            data-review-path={cellReviewPath}
            sx={{
              position: 'relative',
              width: `calc((100% - 64px) * ${widthPercent} / 100)`,
              minWidth: `${getColumnMinWidth(childField.displayWidth)}px`,
              flexShrink: 0,
              padding: hasTextareaChild || hasMatchReasonRow || hasErrorRow ? '8px 4px' : '0 4px',
              boxSizing: 'border-box',
              ...(isColumnReReading
                ? {
                    ...rowStyles.reReadingOverlay,
                    pointerEvents: 'none' as const,
                  }
                : {}),
            }}
          >
            {/* 読み取り信頼度の生数値（#3262）。運営スタッフのみ。
                一般ユーザー向けの要注意表示はセル下部に出す */}
            {isCellUncertain && cellTooltip && (
              <Tooltip title={cellTooltip} arrow placement="right">
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
            )}
            <Box
              sx={{
                display: 'flex',
                // textarea で入力欄が縦に伸びても照合・変換ボタンが
                // 中央につられて下がらないよう上端で揃える（#3130）
                alignItems: 'flex-start',
                gap: '4px',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  // 計算値採用中（#3172）も自動入力欄と同じグレー背景にする。
                  // ここは MUI を使うセル（textarea/日付/選択肢）に効く
                  ...(isAutoFilledCell
                    ? {
                        '& .MuiInputBase-root': {
                          backgroundColor: gray5,
                        },
                      }
                    : {}),
                }}
              >
                {childField.type === 'number' ? (
                  <PlainCellInput
                    numeric
                    value={
                      item[childField.childValue] === null ||
                      item[childField.childValue] === undefined
                        ? ''
                        : String(item[childField.childValue] || '')
                    }
                    onChange={(inputValue: string): void => {
                      // 空文字の場合はnull、それ以外は文字列のまま保持（保存時にカンマ除去・数値変換）
                      setDynamicFormData(prev =>
                        updateCellValue(
                          prev,
                          itemGroup.parentValue,
                          itemIndex,
                          childField.childValue,
                          inputValue === '' ? null : inputValue,
                        ),
                      );
                      const err = validateArrayField(
                        itemGroup.parentValue,
                        itemIndex,
                        childField.childValue,
                        inputValue === '' ? null : inputValue,
                        childField,
                      );
                      updateArrayFieldError(
                        itemGroup.parentValue,
                        rowId,
                        childField.childValue,
                        err,
                      );
                    }}
                    readOnly={isCellCalculationLocked}
                    filled={isAutoFilledCell}
                    dimmed={isColumnReReading}
                    error={
                      !!arrayFieldErrors[
                        arrayFieldErrorKey(itemGroup.parentValue, rowId, childField.childValue)
                      ]
                    }
                    onFocus={(): void =>
                      setFocusItem(
                        `${itemGroup.parentValue}-${item.index}-${childField.childValue}`,
                      )
                    }
                    onBlur={(): void => {
                      setFocusItem('');
                      requestRecalculationForItem(childField.id);
                    }}
                  />
                ) : childField.type === 'date' ? (
                  <CustomDatePicker
                    value={(item[childField.childValue] as Date) || null}
                    onChange={(value: Date | null): void => {
                      setDynamicFormData(prev =>
                        updateCellValue(
                          prev,
                          itemGroup.parentValue,
                          itemIndex,
                          childField.childValue,
                          value,
                        ),
                      );
                      const err = validateArrayField(
                        itemGroup.parentValue,
                        itemIndex,
                        childField.childValue,
                        value,
                        childField,
                      );
                      updateArrayFieldError(
                        itemGroup.parentValue,
                        rowId,
                        childField.childValue,
                        err,
                      );
                      // 日付は選択確定＝onChange なのでここで再計算する（#3172）
                      requestRecalculationForItem(childField.id);
                    }}
                    labelText=""
                    width="100%"
                    readOnly={isCellCalculationLocked}
                    isRequired={false}
                    formMb="zero"
                    heightIs40px
                    paddingIs10px8px
                    fontSizeIs14px
                    hideHelperText
                    onFocus={(): void =>
                      setFocusItem(
                        `${itemGroup.parentValue}-${item.index}-${childField.childValue}`,
                      )
                    }
                    onBlur={(): void => setFocusItem('')}
                    dateFormat={childField.outputFormat}
                  />
                ) : isChoiceDataType(childField.type) ? (
                  // 選択肢型（#3075）。明細の列はプルダウンのみ。
                  // 保持するのは出力値、表示するのは表示名
                  <ChoiceField
                    value={item[childField.childValue] ? String(item[childField.childValue]) : ''}
                    onChange={(next: string): void => {
                      setDynamicFormData(prev =>
                        updateCellValue(
                          prev,
                          itemGroup.parentValue,
                          itemIndex,
                          childField.childValue,
                          next,
                        ),
                      );
                      const err = validateArrayField(
                        itemGroup.parentValue,
                        itemIndex,
                        childField.childValue,
                        next,
                        childField,
                      );
                      updateArrayFieldError(
                        itemGroup.parentValue,
                        rowId,
                        childField.childValue,
                        err,
                      );
                    }}
                    choices={childField.choices ?? []}
                    variant="select"
                    // 計算フィールドは人が選び替えられないようにする（#3358）。
                    // 同じ行の日付・テキストの readOnly と揃える
                    disabled={isCellCalculationLocked}
                    required={childField.required ?? false}
                    height="40px"
                    error={
                      !!arrayFieldErrors[
                        arrayFieldErrorKey(itemGroup.parentValue, rowId, childField.childValue)
                      ]
                    }
                    onFocus={(): void =>
                      setFocusItem(
                        `${itemGroup.parentValue}-${item.index}-${childField.childValue}`,
                      )
                    }
                    onBlur={(): void => setFocusItem('')}
                  />
                ) : // textarea は複数行に伸びる必要があるため MUI のまま。
                // 単一行テキストだけ軽量な input に置き換えて検証する（#3275）
                childField.type !== 'textarea' ? (
                  <PlainCellInput
                    value={item[childField.childValue] ? String(item[childField.childValue]) : ''}
                    onChange={(next: string): void => {
                      setDynamicFormData(prev =>
                        updateCellValue(
                          prev,
                          itemGroup.parentValue,
                          itemIndex,
                          childField.childValue,
                          next,
                        ),
                      );
                      const err = validateArrayField(
                        itemGroup.parentValue,
                        itemIndex,
                        childField.childValue,
                        next,
                        childField,
                      );
                      updateArrayFieldError(
                        itemGroup.parentValue,
                        rowId,
                        childField.childValue,
                        err,
                      );
                    }}
                    readOnly={isCellCalculationLocked}
                    filled={isAutoFilledCell}
                    dimmed={isColumnReReading}
                    error={
                      !!arrayFieldErrors[
                        arrayFieldErrorKey(itemGroup.parentValue, rowId, childField.childValue)
                      ]
                    }
                    onFocus={(): void =>
                      setFocusItem(
                        `${itemGroup.parentValue}-${item.index}-${childField.childValue}`,
                      )
                    }
                    onBlur={(): void => {
                      setFocusItem('');
                      requestRecalculationForItem(childField.id);
                    }}
                  />
                ) : (
                  <CustomTextfield
                    value={item[childField.childValue] ? String(item[childField.childValue]) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                      setDynamicFormData(prev =>
                        updateCellValue(
                          prev,
                          itemGroup.parentValue,
                          itemIndex,
                          childField.childValue,
                          e.target.value,
                        ),
                      );
                      const err = validateArrayField(
                        itemGroup.parentValue,
                        itemIndex,
                        childField.childValue,
                        e.target.value,
                        childField,
                      );
                      updateArrayFieldError(
                        itemGroup.parentValue,
                        rowId,
                        childField.childValue,
                        err,
                      );
                    }}
                    inputWidth="100%"
                    height="40px"
                    labelFontSize="12px"
                    inputPaddingIs10px8px
                    formMb="0px"
                    fontSizeIs14px
                    readOnly={isCellCalculationLocked}
                    error={
                      !!arrayFieldErrors[
                        arrayFieldErrorKey(itemGroup.parentValue, rowId, childField.childValue)
                      ]
                    }
                    multiline
                    minRows={1}
                    onFocus={(): void =>
                      setFocusItem(
                        `${itemGroup.parentValue}-${item.index}-${childField.childValue}`,
                      )
                    }
                    onBlur={(): void => {
                      setFocusItem('');
                      requestRecalculationForItem(childField.id);
                    }}
                  />
                )}
              </Box>
              {mastersBySearchItemId.has(childField.id) && (
                <Button
                  onClick={(): void => {
                    const master = mastersBySearchItemId.get(childField.id);
                    if (!master) return;
                    // 「OCR結果：」表示には常に最初のOCR値を使う。
                    // 自動照合や手動再照合で書き換えられた現在のフォーム値
                    // ではなく、Firestore に保存している ocr_value を優先する。
                    const arr = (
                      resultRecord?.result_data as Record<string, unknown> | undefined
                    )?.[itemGroup.id] as Array<Record<string, unknown>> | undefined;
                    // result_data は保存時点のスナップショットなので行ID で引く（#3283）
                    const storedField = arr?.[rowId]?.[childField.id] as
                      | { ocr_value?: string }
                      | undefined;
                    const formValue = String(
                      dynamicFormData[itemGroup.parentValue]?.[itemIndex]?.[
                        childField.childValue
                      ] ?? '',
                    );
                    const rowOcrValue =
                      storedField?.ocr_value ??
                      // 未保存の再照合で照合キー欄をマスタ値に
                      // 上書きしているため（#3274）、保存前に
                      // 選択済みならその ocr_value を優先する。
                      selectedMasterIds.get(masterSelectionKey(master.master_id, rowId))
                        ?.ocr_value ??
                      formValue;
                    setReconciliationTarget({
                      master,
                      // selectedMasterIds のキーになるため行ID を渡す（#3283）
                      rowIndex: rowId,
                      ocrValue: rowOcrValue,
                      // AI照合の初期クエリは入力欄で編集中の現在値を使う
                      searchQuery: formValue,
                      supplierId: resolveSupplierId(master, rowId),
                      // 絞り込み値は明細行ごとに変わるため行番号を渡す（#3180）
                      filterConditions: resolveFilterConditions(master, rowId),
                    });
                    setMasterModalOpen(true);
                  }}
                  sx={{
                    ...secondaryButtonBackgroundColor,
                    padding: '2px 6px',
                    minWidth: 'auto',
                    height: '40px',
                    gap: '2px',
                    flexShrink: 0,
                  }}
                >
                  <ManageSearchIcon sx={{ fontSize: '14px' }} />
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    照合
                  </Typography>
                </Button>
              )}
              {/* 紐付け解除は照合モーダル内の「照合解除」ボタンに集約（#2896） */}
              {dictionariesBySearchItemId.has(childField.id) && (
                <Button
                  onClick={(): void => {
                    const dictionary = dictionariesBySearchItemId.get(childField.id);
                    if (!dictionary) return;
                    // 「OCR結果：」表示には常に最初のOCR値を使う。
                    const arr = (
                      resultRecord?.result_data as Record<string, unknown> | undefined
                    )?.[itemGroup.id] as Array<Record<string, unknown>> | undefined;
                    // result_data は保存時点のスナップショットなので行ID で引く（#3283）
                    const storedField = arr?.[rowId]?.[childField.id] as
                      | { ocr_value?: string }
                      | undefined;
                    const formValue = String(
                      dynamicFormData[itemGroup.parentValue]?.[itemIndex]?.[
                        childField.childValue
                      ] ?? '',
                    );
                    const rowOcrValue = storedField?.ocr_value ?? formValue;
                    setDictionaryReconciliationTarget({
                      dictionary,
                      // selectedDictionaryHistories のキーになるため行ID を渡す（#3283）
                      rowIndex: rowId,
                      ocrValue: rowOcrValue,
                      // 入力欄の現在値で再変換検索する。
                      searchQuery: formValue,
                    });
                    setDictionaryModalOpen(true);
                  }}
                  sx={{
                    ...secondaryButtonBackgroundColor,
                    padding: '2px 6px',
                    minWidth: 'auto',
                    height: '40px',
                    gap: '2px',
                    flexShrink: 0,
                  }}
                >
                  <MenuBookIcon sx={{ fontSize: '14px' }} />
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    変換
                  </Typography>
                </Button>
              )}
            </Box>
            {isSearchKey &&
              ((): React.ReactElement | null => {
                // マスタ照合の手動再照合ラベルはマスタにのみ存在。
                // 辞書変換は result_data の firestoreLabel を直接表示する。
                const master = mastersBySearchItemId.get(childField.id);
                // 表示優先順:
                //   1) selectedMasterIds（マスタ照合で手動選択し直したラベル）
                //   2) Firestore result_data 内の自動照合/辞書変換ラベル
                const mapKey = master ? masterSelectionKey(master.master_id, rowId) : '';
                const entry = mapKey ? selectedMasterIds.get(mapKey) : undefined;
                const firestoreCell = (
                  fieldResultData?.[itemGroup.id] as Array<Record<string, unknown>> | undefined
                )?.[rowId]?.[childField.id] as
                  | { match_reason_label?: string; match_filter_values?: MatchFilterValue[] }
                  | undefined;
                const firestoreLabel = firestoreCell?.match_reason_label;
                // D-2: 紐付け解除済みなら Firestore 由来ラベルは打ち消す。
                const isUnlinked = mapKey ? unlinkedMasterKeys.has(mapKey) : false;
                const label = entry?.match_reason_label ?? (isUnlinked ? '' : firestoreLabel);
                if (!label) return null;
                // 絞り込みは照合時の値を出す（#3517）。ラベルと同じ出どころから取る
                const filterValues = entry?.match_reason_label
                  ? entry.match_filter_values
                  : firestoreCell?.match_filter_values;
                // セル内に通常配置で表示する。行 Box は alignItems: 'flex-start' に
                // 切り替わるので、フォームの位置は他のセルと縦上揃えで揃う。
                return (
                  <MatchReasonLabel label={label} filterValues={filterValues} marginBottom="2px" />
                );
              })()}
            {/* 確認すべき理由（#2815 / #3172 / #3262）。
                バリデーションエラー・計算エラー・要注意は
                ユーザーから見れば同じ「このセルを確認する理由」なので
                1 行にまとめて出す。行の高さは hasErrorRow で確保する */}
            {((): React.ReactElement | null => {
              const reasons = buildCellReviewReasons(
                arrayFieldErrors[
                  arrayFieldErrorKey(itemGroup.parentValue, rowId, childField.childValue)
                ],
                cellCalculationState?.errorMessage,
                cellReviewIssues,
              );
              if (reasons.length === 0) return null;
              return (
                <Typography
                  sx={{
                    // 入力欄の直下に置く。次の項目との間隔より近くしてグルーピングを保つ（#3262）
                    mt: '2px',
                    mb: '2px',
                    fontSize: '11px',
                    lineHeight: 1.4,
                    color: red50,
                    textAlign: 'left',
                    wordBreak: 'break-word',
                  }}
                >
                  [要注意] {reasons.join(' / ')}
                </Typography>
              );
            })()}
          </Box>
        );
      })}
      <Box
        sx={{
          width: '32px',
          flexShrink: 0,
          textAlign: 'center',
        }}
      >
        <IconButton
          sx={{ padding: '0px' }}
          onClick={(): void => {
            // 配列をコピーしてから挿入する（#3275）。
            // 以前は浅いコピー後に splice しており、元の配列を
            // 破壊的に変更していた（React の不変性違反）
            const emptyRow = createEmptyRow(itemGroup.children, nextRowIdCounter);
            setDynamicFormData(prev => {
              const rows = prev[itemGroup.parentValue] ?? [];
              return {
                ...prev,
                [itemGroup.parentValue]: [
                  ...rows.slice(0, itemIndex + 1),
                  emptyRow,
                  ...rows.slice(itemIndex + 1),
                ],
              };
            });
            setNextRowIdCounter(prev => prev + 1); // カウンターを増加
            // 追加行の計算フィールドを計算する（#3172）
            requestRecalculation();
          }}
        >
          <AddCircleOutlineIcon sx={rowStyles.addButton} />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: '32px',
          flexShrink: 0,
          textAlign: 'center',
        }}
      >
        <IconButton
          sx={{ padding: '0px' }}
          onClick={(): void => {
            // 配列をコピーしてから削除する（#3275）
            setDynamicFormData(prev => {
              const rows = prev[itemGroup.parentValue] ?? [];
              return {
                ...prev,
                [itemGroup.parentValue]: rows.filter((_, index) => index !== itemIndex),
              };
            });
            // 削除した行に紐づく画面状態（エラー・照合結果など）を捨てる（#3283）。
            // キーは行ID ベースなので他の行はズレないが、この行の分は残るため明示的に消す。
            discardRowState(itemGroup, rowId);
            // 行番号がずれるため計算セルの状態を再構築する（#3172）
            requestRecalculation();
          }}
        >
          <RemoveCircleOutlineIcon sx={rowStyles.removeButton} />
        </IconButton>
      </Box>
    </Box>
  );
};

/**
 * この行に関係する値だけを比較する（#3275）。
 *
 * dynamicFormData / arrayFieldErrors / calculationCellStates は明細全体を
 * 保持する state で、どこか1セルを編集すると必ず新しい参照になる。
 * 既定の浅い比較では全行が再描画されてしまうため、
 * 「自分の行のキー」だけを見て変化を判定する。
 *
 * 比較対象から漏れた値は更新されなくなるので、行が参照する state を
 * 増やしたときはこの関数にも追加すること。
 */
const arePropsEqual = (prev: DetailTableRowProps, next: DetailTableRowProps): boolean => {
  // 行の位置・構成が変わったら作り直す
  if (
    prev.itemIndex !== next.itemIndex ||
    prev.item !== next.item ||
    prev.itemGroup !== next.itemGroup ||
    prev.activeChildren !== next.activeChildren ||
    prev.totalFlex !== next.totalFlex ||
    prev.hasTextareaChild !== next.hasTextareaChild
  ) {
    return false;
  }

  // 明細全体で共有され、変わると表示に影響する値
  if (
    prev.fieldResultData !== next.fieldResultData ||
    prev.resultRecord !== next.resultRecord ||
    prev.mastersBySearchItemId !== next.mastersBySearchItemId ||
    prev.dictionariesBySearchItemId !== next.dictionariesBySearchItemId ||
    prev.masterMappingTargetIds !== next.masterMappingTargetIds ||
    prev.dictionaryMappingTargetIds !== next.dictionaryMappingTargetIds ||
    prev.unlinkedMasterKeys !== next.unlinkedMasterKeys ||
    prev.cellReviewIssuesByPath !== next.cellReviewIssuesByPath ||
    prev.reReadingItemIds !== next.reReadingItemIds ||
    prev.isProcessingStatus !== next.isProcessingStatus ||
    prev.user !== next.user ||
    prev.nextRowIdCounter !== next.nextRowIdCounter
  ) {
    return false;
  }

  const { itemGroup, itemIndex } = next;
  // 状態キーは行ID ベース（#3283）。itemIndex は dynamicFormData の位置参照にのみ使う
  const rowId = getRowId(next.item, itemIndex);

  // 自分の行のセル値
  const prevRow = prev.dynamicFormData[itemGroup.parentValue]?.[itemIndex];
  const nextRow = next.dynamicFormData[itemGroup.parentValue]?.[itemIndex];
  if (prevRow !== nextRow) return false;

  // 自分の行のバリデーションエラー・計算セル状態。
  // 行の高さを決める hasErrorRow は activeChildren ではなく
  // itemGroup.children 全体を見ているため、比較もそちらに合わせる（#3275）
  for (const child of itemGroup.children ?? []) {
    const errorKey = arrayFieldErrorKey(itemGroup.parentValue, rowId, child.childValue);
    if (prev.arrayFieldErrors[errorKey] !== next.arrayFieldErrors[errorKey]) return false;

    const calcKey = arrayCalculationCellKey(itemGroup.id, rowId, child.id);
    if (prev.calculationCellStates[calcKey] !== next.calculationCellStates[calcKey]) return false;
  }

  // 照合理由ラベルは行高さにも影響する。
  // マスタを持たない子項目（辞書変換のみの列など）だけの行で取りこぼさないよう、
  // 該当キーが1つも無い場合は参照の変化をそのまま再描画とみなす（#3275）
  if (prev.selectedMasterIds !== next.selectedMasterIds) {
    let checkedAnyMasterKey = false;
    for (const child of itemGroup.children ?? []) {
      const master = next.mastersBySearchItemId.get(child.id);
      if (!master) continue;
      checkedAnyMasterKey = true;
      const masterKey = masterSelectionKey(master.master_id, rowId);
      if (prev.selectedMasterIds.get(masterKey) !== next.selectedMasterIds.get(masterKey)) {
        return false;
      }
    }
    if (!checkedAnyMasterKey) return false;
  }

  return true;
};

export default React.memo(DetailTableRow, arePropsEqual);
